import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Save, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

export default function SetupDealershipSelectorPage({ dealerships: initialDealerships }) {
  const navigate = useNavigate();
  const [dealerships, setDealerships] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [formData, setFormData] = useState({
    company_name: '',
    group_name: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    account_manager_id: '',
    account_executive_id: '',
    oem_id: '',
  });
  const [availableOEMs, setAvailableOEMs] = useState([]);
  const [error, setError] = useState('');

  // Location management state
  const [expandedDealershipId, setExpandedDealershipId] = useState(null);
  const [locations, setLocations] = useState([]);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  
  // Inline editing state for group names
  const [editingId, setEditingId] = useState(null);
  const [editedGroup, setEditedGroup] = useState(null);
  const [saving, setSaving] = useState(false);
  const [locationFormData, setLocationFormData] = useState({
    location_name: '',
    address: '',
    rooftops: 0,
    franchises: [],
    contract_expiry: '',
    leadbox_enabled: false,
  });
  const [franchiseInput, setFranchiseInput] = useState('');
  const [availableBrands, setAvailableBrands] = useState([]);
  const [selectedBrandIds, setSelectedBrandIds] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError('');
        // Run all fetches in parallel, but don't fail all if one fails
        await Promise.allSettled([
          fetchDealerships(),
          fetchUsers(),
          fetchStats(),
          fetchBrands(),
          fetchOEMs(),
          fetchUserRole()
        ]);
      } catch (err) {
        console.error('Error loading initial data:', err);
        setError(`Failed to load data: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [showInactive]);

  const fetchBrands = async () => {
    try {
      const response = await fetch(`${API_URL}/brands`);
      if (response.ok) {
        const brands = await response.json();
        setAvailableBrands(brands);
      }
    } catch (err) {
      console.error('Error fetching brands:', err);
    }
  };

  const fetchOEMs = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/oems`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const oems = await response.json();
        // Filter by is_active if it exists, otherwise include all OEMs
        setAvailableOEMs(oems.filter(o => o.is_active !== false));
        console.log('Fetched OEMs:', oems.length, 'Available:', oems.filter(o => o.is_active !== false).length);
      } else {
        const errorText = await response.text();
        console.error('Failed to fetch OEMs:', response.status, errorText);
      }
    } catch (err) {
      console.error('Error fetching OEMs:', err);
    }
  };

  const fetchUserRole = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setUserRole(data.user?.role || null);
      }
    } catch (error) {
      console.error('Error fetching user role:', error);
    }
  };

  const fetchDealerships = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      // Fetch groups (which are the dealerships in this system)
      const groupsResponse = await fetch(`${API_URL}/groups`, { headers });
      if (!groupsResponse.ok) {
        const errorText = await groupsResponse.text();
        console.error('Groups API error:', groupsResponse.status, errorText);
        throw new Error(`Failed to fetch groups: ${groupsResponse.status} ${groupsResponse.statusText}`);
      }

      const groups = await groupsResponse.json();

      // Fetch all locations to count them per group
      const locationsResponse = await fetch(`${API_URL}/dealer-locations-with-addresses`, { headers });
      if (!locationsResponse.ok) {
        const errorText = await locationsResponse.text();
        console.error('Locations API error:', locationsResponse.status, errorText);
        throw new Error(`Failed to fetch locations: ${locationsResponse.status} ${locationsResponse.statusText}`);
      }

      const allLocations = await locationsResponse.json();

      // Fetch brands for all locations
      let locationBrandsMap = {};
      try {
        // Fetch brands for all locations
        const brandsResponse = await fetch(`${API_URL}/dealer-locations/brands`, { headers });
        if (brandsResponse.ok) {
          const brandsData = await brandsResponse.json();
          // Create a map of location_id -> [brand names]
          brandsData.forEach(item => {
            if (!locationBrandsMap[item.dealer_location_id]) {
              locationBrandsMap[item.dealer_location_id] = [];
            }
            if (item.brand_name) {
              locationBrandsMap[item.dealer_location_id].push(item.brand_name);
            }
          });
        }
      } catch (brandErr) {
        console.warn('Could not fetch brands, continuing without brand search:', brandErr);
      }

      // Count locations per group and get company name from trading_name
      const groupsWithCounts = groups.map(group => {
        const groupLocations = allLocations.filter(loc => loc.group_id === group.id);
        const locationCount = groupLocations.length;
        // Get company name from trading_name of first location, or use group name as fallback
        const companyName = groupLocations.length > 0 && groupLocations[0].trading_name 
          ? groupLocations[0].trading_name 
          : group.name;
        
        // Collect all unique brands/franchises for this group
        const groupBrands = new Set();
        groupLocations.forEach(loc => {
          const brands = locationBrandsMap[loc.id] || [];
          brands.forEach(brand => groupBrands.add(brand));
        });
        const franchises = Array.from(groupBrands);
        
        return {
          ...group,
          location_count: locationCount,
          // Group name is the group name, company name comes from trading_name
          group_name: group.name,
          company_name: companyName,
          name: group.name, // Keep for compatibility
          franchises: franchises, // Array of brand/franchise names
        };
      });

      setDealerships(groupsWithCounts);
      setError(''); // Clear any previous errors
      console.log(`Successfully loaded ${groupsWithCounts.length} dealerships`);
    } catch (err) {
      const errorMessage = err.message || 'Failed to load dealerships';
      setError(`Error loading dealerships: ${errorMessage}. Please check the browser console for details.`);
      console.error('Error fetching dealerships:', err);
      console.error('Error details:', {
        message: err.message,
        stack: err.stack,
        response: err.response
      });
      setDealerships([]); // Set empty array on error
      throw err; // Re-throw so Promise.allSettled can catch it
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const requestHeaders = {
        'Content-Type': 'application/json',
      };
      if (token) {
        requestHeaders['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_URL}/users`, {
        headers: requestHeaders,
      });

      if (!response.ok) throw new Error('Failed to fetch users');

      const data = await response.json();
      setUsers(data.users || data || []);
    } catch (err) {
      console.error('Failed to load users:', err);
    }
  };

  const fetchStats = async () => {
    try {
      // Calculate stats from groups and locations
      const [groupsResponse, locationsResponse] = await Promise.all([
        fetch(`${API_URL}/groups`),
        fetch(`${API_URL}/dealer-locations-with-addresses`)
      ]);

      if (!groupsResponse.ok || !locationsResponse.ok) {
        throw new Error('Failed to fetch stats data');
      }

      const groups = await groupsResponse.json();
      const locations = await locationsResponse.json();

      const stats = {
        total_dealerships: groups.length,
        active_dealerships: groups.length, // Groups don't have is_active in current schema
        inactive_dealerships: 0,
        total_account_managers: 0, // Would need to query users table if needed
      };

      setStats(stats);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const url = `${API_URL}/groups`;

      const token = localStorage.getItem('accessToken');
      const requestBody = {
        id: crypto.randomUUID(),
        name: formData.company_name || formData.group_name,
        oem_id: formData.oem_id && formData.oem_id !== '' ? formData.oem_id : null
      };
      
      console.log('Creating group with data:', requestBody);

      const headers = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        let errorMessage = 'Failed to save dealership group';
        let errorDetails = null;
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
          errorDetails = errorData;
        } catch (e) {
          const errorText = await response.text();
          errorMessage = errorText || errorMessage;
        }
        console.error('Group creation failed:', response.status, errorMessage, errorDetails);
        throw new Error(errorMessage);
      }

      await fetchDealerships();
      await fetchStats();
      handleCloseModal();
    } catch (err) {
      setError(err.message);
    }
  };

  const startEditing = (dealership) => {
    setEditingId(dealership.id);
    setEditedGroup({ 
      ...dealership, 
      name: dealership.group_name || dealership.name,
      oem_id: dealership.oem_id || null
    });
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditedGroup(null);
  };

  const saveEditing = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('accessToken');
      const headers = {
        'Content-Type': 'application/json'
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_URL}/groups/${editedGroup.id}`, {
        method: 'PUT',
        headers: headers,
        body: JSON.stringify({
          name: editedGroup.name,
          oem_id: editedGroup.oem_id || null
        })
      });

      if (response.ok) {
        await fetchDealerships();
        cancelEditing();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update group');
      }
    } catch (error) {
      console.error('Error updating group:', error);
      setError(`Failed to update group: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditedGroup(prev => ({ ...prev, [name]: value }));
  };

  const handleDeleteGroup = async (id) => {
    const dealership = dealerships.find(d => d.id === id);
    const locationCount = dealership?.location_count || 0;
    
    let confirmMessage = `Are you sure you want to delete the group "${dealership?.name}"?`;
    if (locationCount > 0) {
      confirmMessage = `Are you sure you want to delete the group "${dealership?.name}"?\n\nThis will also permanently delete ${locationCount} location(s) assigned to this group.\n\nThis action cannot be undone.`;
    }

    if (!window.confirm(confirmMessage)) return;

    try {
      const response = await fetch(`${API_URL}/groups/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete group');
      }

      await fetchDealerships();
      await fetchStats();
    } catch (err) {
      setError(err.message || 'Failed to delete dealership group');
      console.error(err);
    }
  };

  const handleDeactivateGroup = async (id) => {
    const dealership = dealerships.find(d => d.id === id);
    
    if (!window.confirm(`Are you sure you want to deactivate the group "${dealership?.name}"?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/groups/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          is_active: false
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to deactivate group');
      }

      await fetchDealerships();
      await fetchStats();
      alert(`Group "${dealership?.name}" has been deactivated.`);
    } catch (err) {
      setError(err.message || 'Failed to deactivate dealership group');
      console.error(err);
    }
  };

  const handleReactivate = async (id) => {
    // Groups don't have reactivate in current schema - this would need to be implemented
    setError('Reactivate functionality not available for groups');
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({
      company_name: '',
      group_name: '',
      contact_email: '',
      contact_phone: '',
      address: '',
      account_manager_id: '',
      account_executive_id: '',
      oem_id: '',
    });
    setError('');
    console.log('Modal closed, form reset');
  };

  // Location management functions
  const fetchLocations = async (groupId) => {
    try {
      const response = await fetch(`${API_URL}/dealer-locations-with-addresses`);
      if (!response.ok) throw new Error('Failed to fetch locations');

      const allLocations = await response.json();
      // Filter locations by group_id
      const groupLocations = allLocations
        .filter(loc => loc.group_id === groupId)
        .map(loc => ({
          id: loc.id,
          location_name: loc.name,
          trading_name: loc.trading_name,
          address: loc.street_name 
            ? `${loc.building_number || ''} ${loc.street_name || ''} ${loc.locality || ''} ${loc.town_city || ''} ${loc.postcode || ''}`.trim()
            : '',
          rooftops: 0, // Not in current schema
          franchises: [], // Not in current schema
          contract_expiry: '', // Not in current schema
          leadbox_enabled: false, // Not in current schema
          group_id: loc.group_id,
          address_id: loc.address_id,
          brands: loc.brands || [], // Include brands array from API
        }));

      setLocations(groupLocations);
    } catch (err) {
      console.error('Failed to load locations:', err);
      setLocations([]);
    }
  };

  const toggleDealershipExpanded = async (dealershipId) => {
    if (expandedDealershipId === dealershipId) {
      setExpandedDealershipId(null);
      setLocations([]);
    } else {
      setExpandedDealershipId(dealershipId);
      await fetchLocations(dealershipId);
    }
  };

  const handleAddLocation = (dealershipId) => {
    setEditingLocation(null);
    setLocationFormData({
      location_name: '',
      address: '',
      rooftops: 0,
      franchises: [],
      contract_expiry: '',
      leadbox_enabled: false,
    });
    setFranchiseInput('');
    setSelectedBrandIds([]);
    setExpandedDealershipId(dealershipId);
    setShowLocationModal(true);
  };

  const handleEditLocation = async (location) => {
    setEditingLocation(location);
    setLocationFormData({
      location_name: location.location_name || location.name || '',
      address: location.address || '',
      rooftops: 0,
      franchises: [],
      contract_expiry: '',
      leadbox_enabled: false,
    });
    setFranchiseInput('');
    
    // Fetch existing brand associations for this location
    try {
      const brandsResponse = await fetch(`${API_URL}/dealer-locations/brands`);
      if (brandsResponse.ok) {
        const allLocationBrands = await brandsResponse.json();
        const locationBrands = allLocationBrands
          .filter(lb => lb.dealer_location_id === location.id)
          .map(lb => {
            const brand = availableBrands.find(b => b.name === lb.brand_name);
            return brand ? brand.id : null;
          })
          .filter(Boolean);
        setSelectedBrandIds(locationBrands);
      }
    } catch (err) {
      console.error('Error fetching location brands:', err);
      setSelectedBrandIds([]);
    }
    
    setShowLocationModal(true);
  };

  const handleLocationSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      // Check if we need to create/update address first
      let addressId = editingLocation?.address_id || null;
      
      // For now, we'll create a basic location with group_id
      // Address management would need separate API calls
      const locationData = {
        name: locationFormData.location_name,
        trading_name: locationFormData.location_name,
        group_id: expandedDealershipId,
        address_id: addressId,
      };

      const url = editingLocation
        ? `${API_URL}/dealer-locations/${editingLocation.id}`
        : `${API_URL}/dealer-locations`;

      const response = await fetch(url, {
        method: editingLocation ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(locationData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save location');
      }

      const savedLocation = await response.json();
      // Use UUID directly - database uses UUIDs, not integers
      const locationId = savedLocation?.id || editingLocation?.id;

      console.log('Location saved:', savedLocation);
      console.log('Location ID:', locationId);
      console.log('Selected brand IDs:', selectedBrandIds);

      // Update brand associations - always update, even if empty
      if (locationId) {
        const brandErrors = [];
        
        // First, delete all existing associations
        try {
          const deleteUrl = `${API_URL}/dealer-locations/${locationId}/brands`;
          console.log('Deleting brand associations:', deleteUrl);
          const deleteResponse = await fetch(deleteUrl, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
            }
          });
          if (!deleteResponse.ok && deleteResponse.status !== 404) {
            const errorData = await deleteResponse.json().catch(() => ({}));
            const errorMsg = errorData.error || deleteResponse.statusText || 'Failed to delete existing brand associations';
            console.warn('Failed to delete existing brand associations:', errorMsg);
            brandErrors.push(errorMsg);
          }
        } catch (err) {
          // Log but don't fail - this is not critical
          const errorMsg = err.message || 'Network error deleting brand associations';
          console.warn('Error deleting brand associations:', errorMsg);
          brandErrors.push(errorMsg);
        }

        // Then, create new associations if any are selected
        if (selectedBrandIds.length > 0) {
          for (const brandId of selectedBrandIds) {
            try {
              const postUrl = `${API_URL}/dealer-locations/${locationId}/brands`;
              console.log('Associating brand:', postUrl, { brand_id: brandId });
              const brandResponse = await fetch(postUrl, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({ brand_id: brandId })
              });
              if (!brandResponse.ok) {
                const errorData = await brandResponse.json().catch(() => ({}));
                const errorMsg = errorData.error || brandResponse.statusText || 'Failed to associate brand';
                console.error('Error associating brand:', errorMsg);
                brandErrors.push(errorMsg);
              }
            } catch (err) {
              const errorMsg = err.message || 'Network error associating brand';
              console.error('Error associating brand:', errorMsg);
              brandErrors.push(errorMsg);
            }
          }
        }
        
        // Show errors if any occurred
        if (brandErrors.length > 0) {
          setError(`Location saved, but some brand associations failed: ${brandErrors.join(', ')}`);
        }
      } else {
        console.error('Location ID not found in response:', savedLocation);
        setError('Failed to get location ID from server response. Location may not have been saved correctly.');
      }

      if (expandedDealershipId) {
        await fetchLocations(expandedDealershipId);
      }
      await fetchDealerships(); // Refresh dealership list to update location counts
      handleCloseLocationModal();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteLocation = async (locationId) => {
    if (!window.confirm('Are you sure you want to delete this location?')) return;

    try {
      const response = await fetch(`${API_URL}/dealer-locations/${locationId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete location');
      }

      if (expandedDealershipId) {
        await fetchLocations(expandedDealershipId);
      }
      await fetchDealerships();
    } catch (err) {
      setError(err.message || 'Failed to delete location');
      console.error(err);
    }
  };

  const handleDeactivateLocation = async (locationId) => {
    if (!window.confirm('Are you sure you want to deactivate this location?')) return;

    try {
      const response = await fetch(`${API_URL}/dealer-locations/${locationId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          is_active: false
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to deactivate location');
      }

      if (expandedDealershipId) {
        await fetchLocations(expandedDealershipId);
      }
      await fetchDealerships();
      alert('Location has been deactivated.');
    } catch (err) {
      setError(err.message || 'Failed to deactivate location');
      console.error(err);
    }
  };

  const handleCloseLocationModal = () => {
    setShowLocationModal(false);
    setEditingLocation(null);
    setLocationFormData({
      location_name: '',
      address: '',
      rooftops: 0,
      franchises: [],
      contract_expiry: '',
      leadbox_enabled: false,
    });
    setFranchiseInput('');
    setError('');
  };

  const handleAddFranchise = () => {
    if (franchiseInput.trim() && !locationFormData.franchises.includes(franchiseInput.trim())) {
      setLocationFormData({
        ...locationFormData,
        franchises: [...locationFormData.franchises, franchiseInput.trim()]
      });
      setFranchiseInput('');
    }
  };

  const handleRemoveFranchise = (index) => {
    setLocationFormData({
      ...locationFormData,
      franchises: locationFormData.franchises.filter((_, i) => i !== index)
    });
  };

  const filteredDealerships = dealerships.filter(
    (dealership) => {
      const groupName = dealership.group_name || dealership.name || '';
      const companyName = dealership.company_name || '';
      const franchises = dealership.franchises || [];
      const franchiseString = franchises.join(' ').toLowerCase();
      const searchLower = searchTerm.toLowerCase();
      return groupName.toLowerCase().includes(searchLower) ||
        companyName.toLowerCase().includes(searchLower) ||
        franchiseString.includes(searchLower);
    }
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-xl mb-2">Loading dealerships...</div>
          <div className="text-sm text-gray-500">Please wait while we fetch your data</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dealership Management</h1>
          <p className="text-gray-600 mt-1">Manage your dealerships and their information</p>
        </div>
        <div className="flex items-center gap-3">
          {dealerships.length > 0 && dealerships[0]?.id && (
            <Link
              to={`/setup/${dealerships[0].id}`}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              View Dashboard
            </Link>
          )}
        </div>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-sm text-gray-600 mb-1">Total Dealerships</div>
            <div className="text-3xl font-bold text-gray-900">{stats.total_dealerships || stats.total_clients || 0}</div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-sm text-gray-600 mb-1">Active Dealerships</div>
            <div className="text-3xl font-bold text-green-600">{stats.active_dealerships || stats.active_clients || 0}</div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-sm text-gray-600 mb-1">Inactive Dealerships</div>
            <div className="text-3xl font-bold text-gray-400">{stats.inactive_dealerships || stats.inactive_clients || 0}</div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-sm text-gray-600 mb-1">Account Managers</div>
            <div className="text-3xl font-bold text-blue-600">{stats.total_account_managers || 0}</div>
          </div>
        </div>
      )}

      {/* Actions Bar */}
      <div className="bg-white p-4 rounded-lg shadow mb-6 flex flex-wrap gap-4 items-center justify-between">
        <div className="flex gap-4 items-center flex-1">
          <input
            type="text"
            placeholder="Search by group name, company name, or franchise/brand..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent flex-1 max-w-md"
          />
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="rounded border-gray-300"
            />
            Show inactive
          </label>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowModal(true)}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            + Add Dealership
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          <div className="font-semibold mb-1">Error Loading Dealerships</div>
          <div>{error}</div>
          <button
            onClick={() => {
              setError('');
              setLoading(true);
              fetchDealerships();
            }}
            className="mt-2 text-sm underline hover:text-red-900"
          >
            Retry
          </button>
        </div>
      )}


      {/* Dealerships Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Group Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Company Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                OEM
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Brand/Franchise
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Locations
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredDealerships.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                  {searchTerm
                    ? 'No dealerships match your search'
                    : 'No dealerships found. Create your first dealership to get started.'}
                </td>
              </tr>
            ) : (
              filteredDealerships.map((dealership) => (
                <React.Fragment key={dealership.id}>
                  <tr 
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={(e) => {
                      // Don't navigate if clicking on buttons or input fields
                      if (e.target.tagName === 'BUTTON' || e.target.tagName === 'INPUT' || e.target.closest('button') || e.target.closest('input') || e.target.closest('a')) {
                        return;
                      }
                      navigate(`/setup/${dealership.id}/locations`);
                    }}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingId === dealership.id ? (
                        <input
                          type="text"
                          name="name"
                          value={editedGroup.name}
                          onChange={handleEditChange}
                          className="w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          disabled={saving}
                          placeholder="Enter group name"
                          onClick={(e) => e.stopPropagation()}
                        />
                      ) : (
                        <div>
                          <Link
                            to={`/setup/${dealership.id}/locations`}
                            className="font-medium text-gray-900 hover:text-blue-600"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {dealership.group_name || dealership.name}
                    </Link>
                          <div className="text-xs text-gray-500 mt-1">ID: {dealership.id}</div>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{dealership.company_name || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingId === dealership.id ? (
                        <select
                          name="oem_id"
                          value={editedGroup.oem_id || ''}
                          onChange={handleEditChange}
                          className="w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          disabled={saving}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <option value="">No OEM (Independent)</option>
                          {availableOEMs.map(oem => (
                            <option key={oem.id} value={oem.id}>{oem.name}</option>
                          ))}
                        </select>
                      ) : (
                        <div className="text-sm text-gray-900">
                          {dealership.oem_name ? (
                            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                              {dealership.oem_name}
                            </span>
                          ) : (
                            <span className="text-gray-400 text-xs">Independent</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {dealership.franchises && dealership.franchises.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {dealership.franchises.map((franchise, idx) => (
                              <span
                                key={idx}
                                className="px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800"
                              >
                                {franchise}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleDealershipExpanded(dealership.id);
                        }}
                        className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 hover:bg-blue-200 cursor-pointer"
                      >
                        {expandedDealershipId === dealership.id ? '▼' : '▶'} {dealership.location_count || 0} {(dealership.location_count || 0) === 1 ? 'location' : 'locations'}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                        Active
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" onClick={(e) => e.stopPropagation()}>
                      {editingId === dealership.id ? (
                        <>
                          <button
                            onClick={saveEditing}
                            className="p-1.5 text-green-600 hover:bg-green-100 rounded transition-colors duration-150 mr-2"
                            disabled={saving}
                            title="Save changes"
                          >
                            <Save size={16} />
                          </button>
                          <button
                            onClick={cancelEditing}
                            className="p-1.5 text-gray-500 hover:bg-gray-100 rounded transition-colors duration-150"
                            disabled={saving}
                            title="Cancel editing"
                          >
                            <X size={16} />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              startEditing(dealership);
                            }}
                            className="text-blue-600 hover:text-blue-900 mr-4"
                          >
                            Edit
                          </button>
                          {userRole === 'admin' ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteGroup(dealership.id);
                              }}
                              className="text-red-600 hover:text-red-900"
                            >
                              Delete
                            </button>
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeactivateGroup(dealership.id);
                              }}
                              className="text-orange-600 hover:text-orange-900"
                            >
                              Deactivate
                            </button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                  {expandedDealershipId === dealership.id && (
                    <tr key={`${dealership.id}-locations`} className="bg-gray-50">
                      <td colSpan={6} className="px-6 py-4">
                        <div className="space-y-3">
                          <div className="flex justify-between items-center mb-3">
                            <h3 className="text-lg font-semibold text-gray-900">Locations for {dealership.group_name || dealership.name}</h3>
                            <button
                              onClick={() => handleAddLocation(dealership.id)}
                              className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
                            >
                              + Add Location
                            </button>
                          </div>
                          {locations.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">
                              No locations found. Click "Add Location" to create one.
                            </div>
                          ) : (
                            <div className="bg-white rounded-lg border border-gray-200">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-100">
                                  <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600">Location Name</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600">Trading Name</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600">Brand/Franchise</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600">Address</th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-600">Actions</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                  {locations.map((location) => (
                                    <tr key={location.id} className="hover:bg-gray-50">
                                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{location.location_name}</td>
                                      <td className="px-4 py-3 text-sm text-gray-600">{location.trading_name || '-'}</td>
                                      <td className="px-4 py-3 text-sm">
                                        {location.brands && location.brands.length > 0 ? (
                                          <div className="flex flex-wrap gap-1">
                                            {location.brands.map((brand, idx) => (
                                              <span
                                                key={brand.id || idx}
                                                className="px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800"
                                              >
                                                {brand.name}
                                              </span>
                                            ))}
                                          </div>
                                        ) : (
                                          <span className="text-gray-400">-</span>
                                        )}
                                      </td>
                                      <td className="px-4 py-3 text-sm text-gray-600">{location.address || '-'}</td>
                                      <td className="px-4 py-3 text-sm text-right">
                                        <button
                                          onClick={() => handleEditLocation(location)}
                                          className="text-blue-600 hover:text-blue-900 mr-3"
                                        >
                                          Edit
                                        </button>
                                        {userRole === 'admin' ? (
                                          <button
                                            onClick={() => handleDeleteLocation(location.id)}
                                            className="text-red-600 hover:text-red-900"
                                          >
                                            Delete
                                          </button>
                                        ) : (
                                          <button
                                            onClick={() => handleDeactivateLocation(location.id)}
                                            className="text-orange-600 hover:text-orange-900"
                                          >
                                            Deactivate
                                          </button>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Dealership Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl font-bold mb-6">
                Add New Dealership Group
              </h2>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Group Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.company_name || formData.group_name}
                    onChange={(e) =>
                      setFormData({ ...formData, company_name: e.target.value, group_name: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter dealership group name"
                  />
                  <p className="text-xs text-gray-500 mt-1">This is the name of the dealership group</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    OEM (Optional)
                  </label>
                  <select
                    value={formData.oem_id || ''}
                    onChange={(e) =>
                      setFormData({ ...formData, oem_id: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">No OEM (Independent)</option>
                    {availableOEMs.length === 0 ? (
                      <option disabled>No OEMs available</option>
                    ) : (
                      availableOEMs.map(oem => (
                        <option key={oem.id} value={oem.id}>{oem.name}</option>
                      ))
                    )}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Select the OEM this dealership group is associated with, or leave blank for independent</p>
                </div>

                <div className="flex justify-end gap-4 mt-6">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Create Group
                  </button>
                </div>
              </form>
            </div>
            </div>
        </div>
      )}

      {/* Location Modal */}
      {showLocationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl font-bold mb-6">
                {editingLocation ? 'Edit Location' : 'Add New Location'}
              </h2>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
                  {error}
                </div>
              )}

                <form onSubmit={handleLocationSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Location Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={locationFormData.location_name}
                    onChange={(e) =>
                      setLocationFormData({ ...locationFormData, location_name: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter location name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address (Optional)
                  </label>
                  <textarea
                    value={locationFormData.address}
                    onChange={(e) =>
                      setLocationFormData({ ...locationFormData, address: e.target.value })
                    }
                    rows={2}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter address details"
                  />
                  <p className="text-xs text-gray-500 mt-1">Note: Full address management is available in the Location Configuration page</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Brand/Franchise (Optional)
                  </label>
                  <div className="border border-gray-300 rounded-lg p-3 max-h-48 overflow-y-auto bg-white">
                    {availableBrands.length === 0 ? (
                      <p className="text-sm text-gray-500">No brands available. Add brands in the Admin section.</p>
                    ) : (
                      <div className="space-y-2">
                        {availableBrands.map(brand => (
                          <label key={brand.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                            <input
                              type="checkbox"
                              checked={selectedBrandIds.includes(brand.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedBrandIds([...selectedBrandIds, brand.id]);
                                } else {
                                  setSelectedBrandIds(selectedBrandIds.filter(id => id !== brand.id));
                                }
                              }}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700">{brand.name}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex justify-end gap-4 mt-6">
                  <button
                    type="button"
                    onClick={handleCloseLocationModal}
                    className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    {editingLocation ? 'Update Location' : 'Create Location'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

